import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Phone, Mail, MapPin, Zap, Calendar, MessageSquare, Send, Loader2, User, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, StatusBadge, formatDateTime, phoneToWhatsApp, CHARGER_LABELS, formatValue } from "./statusConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_ICON = (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [editingFlags, setEditingFlags] = useState(false);
  const [flagSelection, setFlagSelection] = useState([]);
  const [savingFlags, setSavingFlags] = useState(false);

  const FLAG_OPTIONS = [
    { key: "site_survey", label: "Needs Site Survey" },
    { key: "fuseboard_upgrade", label: "Fuseboard Upgrade" },
    { key: "cable_extension", label: "Cable Extension" },
    { key: "solar", label: "Solar Interest" },
  ];

  const handleFlagsSave = async () => {
    setSavingFlags(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/leads/${id}/flags`,
        { flags: flagSelection },
        { withCredentials: true }
      );
      setLead((prev) => ({ ...prev, flags: data.flags }));
      setEditingFlags(false);
      toast.success("Flags updated");
    } catch {
      toast.error("Couldn't update flags");
    } finally {
      setSavingFlags(false);
    }
  };
  const [editingValue, setEditingValue] = useState(false);
  const [valueInput, setValueInput] = useState("");
  const [savingValue, setSavingValue] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads/${id}`, { withCredentials: true });
      setLead(data);
    } catch {
      toast.error("Couldn't load this lead");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const handleStatusChange = async (newStatus) => {
    if (!lead || newStatus === lead.status) return;
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/admin/leads/${id}/status`, { status: newStatus }, { withCredentials: true });
      setLead((prev) => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${STATUS_META[newStatus].label}`);
    } catch {
      toast.error("Couldn't update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const { data: note } = await axios.post(
        `${API}/admin/leads/${id}/notes`,
        { text: noteText.trim() },
        { withCredentials: true }
      );
      setLead((prev) => ({ ...prev, notes: [...(prev.notes || []), note] }));
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Couldn't add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleValueSave = async () => {
    if (!valueInput || isNaN(parseInt(valueInput, 10))) return;
    setSavingValue(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/leads/${id}/value`,
        { lead_value: parseInt(valueInput, 10) },
        { withCredentials: true }
      );
      setLead((prev) => ({ ...prev, lead_value: data.lead_value }));
      setEditingValue(false);
      toast.success("Value updated");
    } catch {
      toast.error("Couldn't update value");
    } finally {
      setSavingValue(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true });
      toast.success("Lead deleted");
      navigate("/admin");
    } catch {
      toast.error("Couldn't delete lead");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-7 h-7 text-[#F5C518] animate-spin" />
      </div>
    );
  }

  if (!lead) return null;

  const waNumber = phoneToWhatsApp(lead.phone);
  const waLink = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi ${lead.name.split(" ")[0]}, thanks for your EV charger enquiry with LK Electrical & Security. `)}`
    : null;

  const photos = [
    { key: "fuseboard_image", label: "Fuseboard" },
    { key: "meter_image", label: "Meter" },
    { key: "location_image", label: "Location" },
  ].filter((p) => lead[p.key]);

  return (
    <div data-testid="lead-detail">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-6 transition-colors"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column: lead info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#18181b] rounded-xl border border-[#27272a] p-6" data-testid="lead-info-card">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-white" data-testid="lead-name">{lead.name}</h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Received {formatDateTime(lead.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={lead.status || "new"} />
                <button
                  onClick={handleDelete}
                  className="p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  data-testid="delete-lead-button"
                  aria-label="Delete lead"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 bg-[#0f0f10] rounded-lg p-3.5 border border-[#27272a]">
                <Phone className="w-4 h-4 text-[#F5C518] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-gray-500 text-xs">Phone</p>
                  <a href={`tel:${lead.phone}`} className="text-white font-medium hover:text-[#F5C518] transition-colors" data-testid="lead-phone">
                    {lead.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-[#0f0f10] rounded-lg p-3.5 border border-[#27272a]">
                <Mail className="w-4 h-4 text-[#F5C518] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-gray-500 text-xs">Email</p>
                  <a href={`mailto:${lead.email}`} className="text-white font-medium hover:text-[#F5C518] transition-colors truncate block" data-testid="lead-email">
                    {lead.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-[#0f0f10] rounded-lg p-3.5 border border-[#27272a]">
                <MapPin className="w-4 h-4 text-[#F5C518] flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-xs">Eircode</p>
                  <p className="text-white font-medium uppercase" data-testid="lead-eircode">{lead.eircode || "Not provided"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-[#0f0f10] rounded-lg p-3.5 border border-[#27272a]">
                <Zap className="w-4 h-4 text-[#F5C518] flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-xs">Charger</p>
                  <p className="text-white font-medium" data-testid="lead-charger">{CHARGER_LABELS[lead.charger_preference] || lead.charger_preference}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-[#0f0f10] rounded-lg p-3.5 border border-[#27272a]">
                <Zap className="w-4 h-4 text-[#F5C518] flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-xs">Pre-wired</p>
                  <p className="text-white font-medium" data-testid="lead-prewired">
                    {lead.prewired === "yes" ? "Yes, pre-wired" : "No / Not sure"}
                  </p>
                </div>
              </div>
            </div>

            {/* Value + consent + flags strip */}
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="lead-meta-strip">
              {editingValue ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={valueInput}
                    onChange={(e) => setValueInput(e.target.value)}
                    className="bg-[#0f0f10] border border-[#F5C518]/40 text-[#F5C518] text-sm font-bold px-3 py-1.5 rounded-full w-28 focus:outline-none"
                    data-testid="lead-value-input"
                    autoFocus
                  />
                  <button onClick={handleValueSave} disabled={savingValue} className="text-xs font-bold bg-[#F5C518] text-black px-3 py-1.5 rounded-full disabled:opacity-50">Save</button>
                  <button onClick={() => { setEditingValue(false); setValueInput(String(lead.lead_value ?? "")); }} className="text-xs text-gray-500 hover:text-white">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingValue(true); setValueInput(String(lead.lead_value ?? "")); }}
                  className="inline-flex items-center gap-1.5 bg-[#F5C518]/10 text-[#F5C518] border border-[#F5C518]/25 text-sm font-bold px-3 py-1.5 rounded-full hover:bg-[#F5C518]/20 transition-colors"
                  data-testid="lead-value"
                >
                  Value {formatValue(lead.lead_value)}
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                </button>
              )}
              {lead.whatsapp_consent ? (
                <span className="inline-flex items-center gap-1.5 bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/25 text-sm font-semibold px-3 py-1.5 rounded-full" data-testid="lead-consent-yes">
                  <MessageSquare className="w-3.5 h-3.5" />
                  WhatsApp OK
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-white/5 text-gray-500 border border-[#27272a] text-sm px-3 py-1.5 rounded-full" data-testid="lead-consent-no">
                  <MessageSquare className="w-3.5 h-3.5" />
                  No WhatsApp consent
                </span>
              )}
              {editingFlags ? (
                <div className="flex flex-wrap items-center gap-2 w-full mt-1">
                  {FLAG_OPTIONS.map((opt) => (
                    <label key={opt.key} className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                      flagSelection.includes(opt.key)
                        ? "bg-[#F5C518]/15 text-[#F5C518] border-[#F5C518]/40"
                        : "bg-white/5 text-gray-400 border-[#27272a] hover:border-[#3f3f46]"
                    }`}>
                      <input
                        type="checkbox"
                        checked={flagSelection.includes(opt.key)}
                        onChange={() => setFlagSelection((s) => s.includes(opt.key) ? s.filter((f) => f !== opt.key) : [...s, opt.key])}
                        className="hidden"
                      />
                      {opt.label}
                    </label>
                  ))}
                  <button onClick={handleFlagsSave} disabled={savingFlags} className="text-xs font-bold bg-[#F5C518] text-black px-3 py-1.5 rounded-full disabled:opacity-50">Save</button>
                  <button onClick={() => { setEditingFlags(false); setFlagSelection(lead.flags || []); }} className="text-xs text-gray-500 hover:text-white">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingFlags(true); setFlagSelection(lead.flags || []); }}
                  className="inline-flex items-center gap-1.5 bg-white/5 text-gray-300 border border-[#27272a] text-sm font-semibold px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors"
                  data-testid="lead-flags"
                >
                  {(lead.flags || []).length > 0 ? (lead.flags || []).map((f) => FLAG_OPTIONS.find((o) => o.key === f)?.label || f).join(", ") : "Add flags"}
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                </button>
              )}
            </div>

            {/* Quick actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <a
                href={`tel:${lead.phone}`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-3 px-5 rounded-lg transition-all"
                data-testid="call-lead-button"
              >
                <Phone className="w-4 h-4" />
                Call {lead.name.split(" ")[0]}
              </a>
              {waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-[#25D366] font-bold py-3 px-5 rounded-lg transition-all"
                  data-testid="whatsapp-lead-button"
                >
                  {WHATSAPP_ICON}
                  WhatsApp {lead.name.split(" ")[0]}
                </a>
              )}
            </div>
          </div>

          {/* Photos */}
          <div className="bg-[#18181b] rounded-xl border border-[#27272a] p-6" data-testid="lead-photos-card">
            <h2 className="text-lg font-bold text-white mb-4">Uploaded Photos</h2>
            {photos.length === 0 ? (
              <p className="text-gray-500 text-sm italic" data-testid="no-photos">No photos were uploaded with this enquiry.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <a
                    key={photo.key}
                    href={lead[photo.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                    data-testid={`photo-${photo.label.toLowerCase()}`}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden border border-[#27272a] group-hover:border-[#F5C518]/50 transition-colors">
                      <img src={lead[photo.key]} alt={photo.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <p className="text-gray-400 text-xs text-center mt-2">{photo.label}</p>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-[#18181b] rounded-xl border border-[#27272a] p-6" data-testid="lead-notes-card">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#F5C518]" />
              Notes
            </h2>

            <form onSubmit={handleAddNote} className="mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note... (e.g. called, arranged survey for Friday)"
                  className="flex-1 bg-[#0f0f10] border border-[#27272a] text-white rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all"
                  data-testid="note-input"
                />
                <button
                  type="submit"
                  disabled={addingNote || !noteText.trim()}
                  className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="add-note-button"
                >
                  {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </form>

            {(!lead.notes || lead.notes.length === 0) ? (
              <p className="text-gray-500 text-sm italic" data-testid="no-notes">No notes yet. Keep track of calls and arrangements here.</p>
            ) : (
              <div className="space-y-3" data-testid="notes-list">
                {[...lead.notes].reverse().map((note) => (
                  <div key={note.id} className="bg-[#0f0f10] rounded-lg p-4 border border-[#27272a]" data-testid={`note-${note.id}`}>
                    <p className="text-gray-200 text-sm">{note.text}</p>
                    <p className="text-gray-600 text-xs mt-2 flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      {note.author} · {formatDateTime(note.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column: status pipeline */}
        <div className="space-y-6">
          <div className="bg-[#18181b] rounded-xl border border-[#27272a] p-6" data-testid="status-pipeline-card">
            <h2 className="text-lg font-bold text-white mb-1">Pipeline Status</h2>
            <p className="text-gray-500 text-xs mb-5">Move this lead through your sales process.</p>
            <div className="space-y-2">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <button
                  key={key}
                  onClick={() => handleStatusChange(key)}
                  disabled={updatingStatus}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-left disabled:opacity-60 ${
                    (lead.status || "new") === key
                      ? "border-current"
                      : "border-[#27272a] hover:border-[#3f3f46]"
                  }`}
                  style={(lead.status || "new") === key ? { color: meta.color, backgroundColor: meta.bg } : {}}
                  data-testid={`status-option-${key}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                  <span className={`font-semibold text-sm ${(lead.status || "new") === key ? "" : "text-gray-300"}`}>
                    {meta.label}
                  </span>
                  {(lead.status || "new") === key && (
                    <span className="ml-auto text-xs font-medium">Current</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
